import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../state/store";
import { toast, ToastContainer } from "react-toastify";
import { LoadingMask } from "../../LoadingMask";
import Button from "../../Button";
import { PlusIcon } from "@heroicons/react/24/outline";
import Table from "../../Table";
import { useEffect, useMemo, useState } from "react";
import { Toast } from "../../Toast";
import { DocumentTemplate } from "../../../models/ConsentDocument";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import { AgencyDocusignTemplateModal } from "./AgencyDocusignTemplateModal";
import { LanguageTypeLabel } from "../../../enums/LanguageType";
import { deleteDocumentTemplate } from "../../../state/templates/actions";
import { DeleteAgencyDocusignTemplate } from "./DeleteAgencyDocusignTemplate";
import { firebaseApp, getStorage } from "../../../firebase/firebaseConfig";
import { deleteObject, ref } from "firebase/storage";

export const AgencyDocusignTemplatesModule = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const isLoadingTemplates = useSelector((state: RootState) => state.templates.isLoading);
    const templates = useSelector((state: RootState) => state.templates.templates);
    const templatesError = useSelector((state: any) => state.templates.error);
    const [showTemplateError, setShowTemplateError] = useState(false);
    const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);

    const [showDocusignTemplateModal, setShowDocusignTemplateModal] = useState<{
        show: boolean;
        update: boolean;
        template?: DocumentTemplate;
    }>({ show: false, update: false });

    const [ showDeleteDocusignTemplateModal, setShowDeleteDocusignTemplateModal] = useState<{
        template?: DocumentTemplate;
        show: boolean;
    }>({ show: false, template: undefined });

    useEffect(() => {
        if (showTemplateError) {
            Toast(t(templatesError?.reason ?? "SOMETHING_WENT_WRONG"), t(templatesError?.cause_info));
            setShowTemplateError(false);
        }
    }, [showTemplateError, templatesError, t]);

    const isLoading = useMemo(() => {
        return (isLoadingTemplates || isDeletingTemplate);
      }, [isDeletingTemplate, isLoadingTemplates]);

    const getTemplatesRows = useMemo(() => {
        return templates?.map((template: DocumentTemplate) => {
            return {
                columns: [
                    { value: template.name },
                    { value: template.description ?? "-" },
                    { value: template.document_name },
                    { value: `${LanguageTypeLabel(template.language)}` },
                    {
                        value: (
                            <div className="flex flex-row justify-center divide-x">
                                <Button
                                    isTerciary
                                    iconOnly
                                    tooltip={t("UPDATE_TEMPLATE")}
                                    onClick={() => {setShowDocusignTemplateModal({show: true, update: true, template: template})}}
                                    classNames="mr-2"
                                >
                                    <PencilIcon className="h-5 aspect-square text-blue" />
                                </Button>
                                <Button
                                    isTerciary
                                    iconOnly
                                    tooltip={t("DELETE_TEMPLATE")}
                                    onClick={() => {setShowDeleteDocusignTemplateModal({show: true, template: template})}}
                                    classNames="mx-2"
                                >
                                    <TrashIcon className="h-5 aspect-square text-red-600" />
                                </Button>
                            </div>
                        ),
                    },
                ],
            };
        });
    }, [templates, t]);

    const onDeleteDocusignTemplate = async (template: DocumentTemplate) => {
        setIsDeletingTemplate(true);
        const storage = getStorage(firebaseApp);
        const fileRef = ref(storage, `templates/${template.document_name}`);

        try {
            await deleteObject(fileRef)
            dispatch(deleteDocumentTemplate({templateId: template.id})).then((e) => {
                if (e.type === "docusign/deleteDocumentTemplate/rejected") {
                    setShowTemplateError(true);
                    setIsDeletingTemplate(false);
                } else {
                    toast(t("SUCCESSFULLY_DELETE_DOCUSIGN_TEMPLATE"));
                    setIsDeletingTemplate(false);
                }
            });
        } catch(error) {
            setShowTemplateError(true);
            setIsDeletingTemplate(false);
        }
    };
    
    return(
        <>
            {isLoading && <LoadingMask />}
            <ToastContainer progressStyle={{ background: "#D4AF37" }} />
            <AgencyDocusignTemplateModal
                info={showDocusignTemplateModal}
                onClose={() => setShowDocusignTemplateModal({show: false, update: false, template: undefined})}
            />
            <DeleteAgencyDocusignTemplate
                info={showDeleteDocusignTemplateModal}
                onClose={() => setShowDeleteDocusignTemplateModal({show: false, template: undefined})}
                deleteTemplate={(template: DocumentTemplate) => onDeleteDocusignTemplate(template)}
            />
            <div className="text-black max-h-full p-5 md:w-full">
                <div className="flex justify-between w-full">
                    <div className="text-md font-bold text-black pl-3 mt-2">
                        {t("DOCUSIGN_TEMPLATES")}
                    </div>
                    <Button onClick={() => setShowDocusignTemplateModal({show: true, update: false, template: undefined})}>
                        <span className="flex items-center font-semibold pr-3">
                            <div className="w-8 p-1 aspect-square mr-2">
                                <PlusIcon />
                            </div>
                            {t("ADD_TEMPLATE")}
                        </span>
                    </Button>
                </div>
                <div className="w-full my-4">
                    <Table
                        columns={[
                            { header: `${t("NAME")}` },
                            { header: `${t("DESCRIPTION")}` },
                            { header: `${t("DOCUMENT_NAME")}` },
                            { header: `${t("LANGUAGE")}` },
                            { header: "" },
                        ]}
                        data={getTemplatesRows}
                    />
                </div>
            </div>
        </>
    )
}